import { Component, inject, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { IndentListService } from './indent-list.service';
import { InventoryService } from '../inventory/inventory.service';

import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Router } from '@angular/router';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { PoService } from '../po/po.service';
import { PaginationComponent } from '../Common/pagination/pagination.component';
import { ToastrService } from 'ngx-toastr';
import {
  NgMultiLabelTemplateDirective,
  NgOptionTemplateDirective,
  NgSelectComponent,
} from '@ng-select/ng-select';
import {
  WarehouseSelectComponent,
  WarehouseOption,
} from '../../shared/components/warehouse-select/warehouse-select.component';
declare var bootstrap: any;

@Component({
  selector: 'app-indent-list',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    NgSelectComponent,
    NgOptionTemplateDirective,
    NgMultiLabelTemplateDirective,
    WarehouseSelectComponent,
    PaginationComponent,
  ],
  providers: [DatePipe],
  templateUrl: './indent-list.component.html',
  styleUrl: './indent-list.component.scss',
})
export class IndentListComponent implements OnInit {
  // Inject services
  productSearch$ = new Subject<string>();
  private indentListService = inject(IndentListService);
  private poService = inject(PoService);
  private locationService = inject(InventoryService);
  private toastr = inject(ToastrService);
  private router = inject(Router);
  public selectedIndentType: string = '';
  // Warehouse chosen in the Generate-PO confirm dialog (multi-warehouse).
  selectedWarehouse: WarehouseOption | null = null;

  onWarehouseChange(warehouse: WarehouseOption | null) {
    this.selectedWarehouse = warehouse;
  }

  // Component properties
  searchTerm = '';
  currentPage = 1;
  pageSize = 10;
  products: any[] = [];
  pageCount = 1;
  selectedLocations: any = [];
  selectedProduct: any = null;
  loading = false;
  isSearching = false;
  indentList: any[] = [];
  fromDate: string | null = null;
  // Form fields
  indentQuantity = 0;
  safetyStock = 0;
  leadTime = 0;
  priorityLevel = 'Medium';

  entries: any[] = [];
  locations: any[] = [];

  constructor() {
    this.getAllEntries();
    this.getAllStores();
  }

  ngOnInit(): void {
    this.getAllStores();
    this.productSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((term) => !!term && term.length >= 3),
        tap(() => (this.isSearching = true)),
        switchMap((term) =>
          this.poService.searchProducts(term).pipe(
            map((res: any) => res?.data || []),
            catchError(() => of([])),
            tap(() => (this.isSearching = false)),
          ),
        ),
      )
      .subscribe((data) => {
        this.products = data;
      });
  }

  getAllEntries() {
    // Implementation for getting all entries if needed
  }

  getAllStores() {
    this.locationService.getAllStores().subscribe({
      next: (data: any) => {
        this.locations = data || [];

        const storedStoreId = localStorage.getItem('storeId');

        if (storedStoreId) {
          const storeExists = this.locations.some(
            (loc) => String(loc.id) === String(storedStoreId),
          );
          if (storeExists) {
            this.selectedLocations = [storedStoreId];
            console.log(
              'Auto-selected store from localStorage:',
              storedStoreId,
            );
            this.onLocationChange();
          } else {
            this.setDefaultStore();
          }
        } else {
          this.setDefaultStore();
        }
      },
      error: (err) => {
        console.error('Error fetching locations', err);
        this.toastr.error('Failed to load locations');
      },
    });
  }

  setDefaultStore() {
    if (this.locations.length > 0) {
      // Select all stores in the list by default
      this.selectedLocations = this.locations.map((loc) => loc.id);
      this.onLocationChange();
    }
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.pageCount }, (_, i) => i + 1);
  }

  trackById = (_: number, item: any) => item.id;

  private recalcPagination(resetToFirst = false) {
    this.pageCount = Math.max(
      1,
      Math.ceil(this.indentList.length / this.pageSize),
    );
    if (resetToFirst) this.currentPage = 1;
    if (this.currentPage > this.pageCount) this.currentPage = this.pageCount;
    if (this.currentPage < 1) this.currentPage = 1;
  }

  onLocationChange() {
    if (!this.selectedLocations || this.selectedLocations.length === 0) {
      this.indentList = [];
      this.recalcPagination(true);
      return;
    }

    const dateParam =
      this.fromDate && this.fromDate.trim() ? this.fromDate : undefined;

    this.indentListService
      .getIndentByStoreIds(this.selectedLocations, dateParam)
      .subscribe({
        next: (res: any) => {
          const indentData = Array.isArray(res) ? res : [];
          this.indentList = indentData.map((item: any) => ({
            id: item.id,
            indentNumber: item.indentNumber || '-',
            storeName: item.store?.name || '-',
            productName: item.variant?.product?.name || '-',
            quantity: item.quantity || 0,
            date: item.createdAt || '-',
            status: item.status,
            sku: item.variant?.product?.sku || '-',
          }));
          this.recalcPagination(true);
          if (this.indentList.length > 0)
            this.toastr.success('Indent list loaded successfully');
          else this.toastr.info('No indents available for this store');
        },
        error: () => this.toastr.error('Failed to load indent list'),
      });
  }

  onPageSizeChange(size: number) {
    this.pageSize = Number(size);
    this.recalcPagination(true); // sets currentPage=1 and clamps
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.pageCount || page === this.currentPage) return;
    this.currentPage = page;
  }
  get pagedIndentList() {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.indentList.slice(start, start + this.pageSize);
  }

  onCreatePo() {
    const storeId = localStorage.getItem('user');

    if (!storeId) {
      this.toastr.error('Store ID not found in local storage.');
      return;
    }

    // Show the Bootstrap modal
    const modalElement = document.getElementById('confirmPoModal');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  // confirmPoGeneration() {
  //   const modalElement = document.getElementById('confirmPoModal');
  //   if (modalElement) {
  //     const modal = bootstrap.Modal.getInstance(modalElement);
  //     if (modal) {
  //       modal.hide();
  //     }
  //   }

  //   const userString = localStorage.getItem('user');

  //   if (!userString) {
  //     this.toastr.error(
  //       'User data not found in local storage. Please log in again.'
  //     );
  //     return;
  //   }

  //   let user;
  //   try {
  //     user = JSON.parse(userString);
  //   } catch (error) {
  //     console.error('Failed to parse user data from local storage:', error);
  //     this.toastr.error(
  //       'Error parsing user data. Please try logging in again.'
  //     );
  //     return;
  //   }

  //   let storeId: string | null = null;

  //   if (user.storeId) {
  //     storeId = user.storeId;
  //   } else if (user.storeAccess && user.storeAccess.length > 0) {
  //     storeId = user.storeAccess[0].storeId;
  //   }

  //   if (!storeId) {
  //     this.toastr.error('Store ID not found in user data.');
  //     console.error('Store ID could not be retrieved from user object.');
  //     return;
  //   }

  //   this.loading = true;
  //   console.log(`Using storeId: ${storeId}`); // Log the ID being used
  //   const payload: any = {
  //     warehouseStoreId: storeId,
  //     indentType: this.selectedIndentType,
  //   };

  //   this.poService.generatePoFromIndents(payload).subscribe({
  //     next: (response: any) => {
  //       this.loading = false;
  //       console.log('PO generated successfully:', response);
  //       this.toastr.success(response.message);
  //       this.router.navigate(['/po']);
  //     },
  //     error: (err) => {
  //       this.loading = false;
  //       console.error('Error generating PO:', err);
  //       this.toastr.error('Failed to create Purchase Order. Please try again.');
  //     },
  //   });
  // }
  confirmPoGeneration() {
    // Hide the modal after the user clicks 'Generate'.
    const modalElement = document.getElementById('confirmPoModal');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }

    // Retrieve and parse user data from local storage.
    const userString = localStorage.getItem('user');
    if (!userString) {
      this.toastr.error(
        'User data not found in local storage. Please log in again.',
      );
      return;
    }

    let user;
    try {
      user = JSON.parse(userString);
    } catch (error) {
      console.error('Failed to parse user data from local storage:', error);
      this.toastr.error(
        'Error parsing user data. Please try logging in again.',
      );
      return;
    }

    // The per-document warehouse picker (confirm dialog) decides which
    // warehouse the POs are raised for — never an arbitrary access-list row.
    const warehouseStoreId = this.selectedWarehouse?.storeId ?? null;
    if (!warehouseStoreId) {
      this.toastr.error('Select the warehouse to raise the POs for.');
      return;
    }

    // Construct the correct payload.
    // We use this.selectedIndentType which is set by the radio button.
    const payload = {
      warehouseStoreId: warehouseStoreId,
      indentType: this.selectedIndentType,
    };

    this.loading = true;
    console.log(
      `Using storeId: ${warehouseStoreId} with indentType: ${this.selectedIndentType}`,
    );

    // Call the service with the new payload.
    this.poService
      .generatePoFromIndents(payload.warehouseStoreId, payload.indentType)
      .subscribe({
        next: (response: any) => {
          this.loading = false;
          console.log('PO generated successfully:', response);
          this.toastr.success(response.message);
          this.router.navigate(['/po']);
        },
        error: (err) => {
          this.loading = false;
          console.error('Error generating PO:', err);
          this.toastr.error(
            'Failed to create Purchase Order. Please try again.',
          );
        },
      });
  }
}
